import React, { useState, useEffect } from 'react';
import { 
    Form, 
    Input, 
    Button, 
    message, 
    Space, 
    Row, 
    Col, 
    Upload, 
    Select,
    Spin,
    Table,
    Popconfirm,
    Modal,
    Tag,
    Avatar,
    Dropdown,
    Menu,
    Typography,
    Tooltip,
    Divider,
    Alert
} from 'antd';
import { 
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PhoneOutlined,
    MailOutlined,
    UserOutlined,
    UploadOutlined,
    IdcardOutlined,
    MoreOutlined,
    QuestionCircleOutlined,
    GlobalOutlined,
    SaveOutlined
} from '@ant-design/icons';
import agentService from '../../services/agentService';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import PageHeader from '../../components/Common/PageHeader';
import env from '../../env';

const { Option } = Select;
const { Text, Title } = Typography;

const languagesOptions = [
    'Urdu',
    'English',
    'Punjabi',
    'Sindhi',
    'Pashto',
    'Balochi',
    'Hindko',
    'Saraiki',
    'Kashmiri',
    'Other'
];

const Agents = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [photoFileList, setPhotoFileList] = useState([]);
    const [editingAgent, setEditingAgent] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState([]);

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            setInitialLoading(true);
            const response = await agentService.getAgents();
            if (response.agents) {
                setAgents(response.agents);
            }
        } catch (error) {
            message.error('Failed to load agents');
        } finally {
            setInitialLoading(false);
        }
    };

    const handlePhotoUpload = (info) => {
        let fileList = [...info.fileList];
        
        // Handle file deletion
        if (info.file.status === 'removed') {
            setPhotoFileList([]);
            return;
        }
        
        // Keep only the last file (limit to 1)
        fileList = fileList.slice(-1);
        
        // Map file list to ensure proper structure
        fileList = fileList.map(file => {
            if (file.response) {
                file.url = file.response.url;
            }
            // Ensure status is set for existing files
            if (!file.status && file.url) {
                file.status = 'done';
            }
            return file;
        });
        
        setPhotoFileList(fileList);
    };

    const handleAdd = () => {
        setEditingAgent(null);
        form.resetFields();
        setPhotoFileList([]);
        setSelectedLanguages([]);
        setIsModalVisible(true);
    };

    const handleEdit = (agent) => {
        setEditingAgent(agent);
        
        if (agent.languages_spoken && Array.isArray(agent.languages_spoken)) {
            setSelectedLanguages(agent.languages_spoken);
        } else {
            setSelectedLanguages([]);
        }
        
        form.setFieldsValue({
            agent_name: agent.agent_name,
            designation: agent.designation,
            phone_whatsapp: agent.phone_whatsapp,
            email: agent.email,
            languages_spoken: agent.languages_spoken && Array.isArray(agent.languages_spoken) 
                ? agent.languages_spoken 
                : [],
        });

        if (agent.photo_url) {
            const photoUrl = agent.photo_url.startsWith('http') 
                ? agent.photo_url 
                : env.BACKEND_URL + agent.photo_url;
            setPhotoFileList([{
                uid: '-1',
                name: 'agent_photo.jpg',
                status: 'done',
                url: photoUrl,
            }]);
        } else {
            setPhotoFileList([]);
        }

        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await agentService.deleteAgent(id);
            message.success('Agent deleted successfully');
            fetchAgents();
        } catch (error) {
            message.error('Failed to delete agent');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            const photoFile = photoFileList.length > 0 && photoFileList[0].originFileObj 
                ? photoFileList[0].originFileObj 
                : null;

            const formData = {
                ...values,
                languages_spoken: selectedLanguages,
            };

            // Handle photo deletion or upload
            if (editingAgent && photoFileList.length === 0 && editingAgent.photo_url) {
                // Photo was removed - send remove_photo flag
                formData.remove_photo = true;
            } else if (photoFile) {
                // New photo uploaded
                formData.photo = photoFile;
            }

            if (editingAgent) {
                await agentService.updateAgent(editingAgent.id, formData);
                message.success('Agent updated successfully');
            } else {
                await agentService.createAgent(formData);
                message.success('Agent created successfully');
            }

            setIsModalVisible(false);
            form.resetFields();
            setPhotoFileList([]);
            setSelectedLanguages([]);
            fetchAgents();
        } catch (error) {
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    const errorMessages = Array.isArray(errors[key]) ? errors[key] : [errors[key]];
                    errorMessages.forEach(msg => {
                        message.error(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${msg}`);
                    });
                });
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Failed to save agent');
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Photo',
            dataIndex: 'photo_url',
            key: 'photo',
            width: 80,
            render: (photoUrl, record) => {
                if (photoUrl) {
                const url = photoUrl.startsWith('http') ? photoUrl : env.BACKEND_URL + photoUrl;
                return (
                    <img 
                        src={url} 
                        alt="Agent" 
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '50%' }}
                    />
                    );
                }
                // Show avatar with first letter if no photo
                const firstLetter = record.agent_name 
                    ? record.agent_name.charAt(0).toUpperCase() 
                    : 'A';
                return (
                    <Avatar 
                        size={50}
                        shape="circle"
                        style={{ 
                            backgroundColor: '#1890ff',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {firstLetter}
                    </Avatar>
                );
            }
        },
        {
            title: 'Agent Name',
            dataIndex: 'agent_name',
            key: 'agent_name',
        },
        {
            title: 'Designation',
            dataIndex: 'designation',
            key: 'designation',
        },
        {
            title: 'Phone / WhatsApp',
            dataIndex: 'phone_whatsapp',
            key: 'phone_whatsapp',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Languages',
            dataIndex: 'languages_spoken',
            key: 'languages_spoken',
            width: 300,
            render: (languages) => {
                if (!languages || !Array.isArray(languages) || languages.length === 0) {
                    return '-';
                }
                return (
                    <Space wrap>
                        {languages.map((lang, index) => (
                            <Tag key={index}>{lang}</Tag>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => {
                const handleMenuClick = ({ key }) => {
                    if (key === 'edit') {
                        handleEdit(record);
                    } else if (key === 'delete') {
                        // Show popconfirm for delete
                        Modal.confirm({
                            title: 'Delete Agent',
                            content: 'Are you sure you want to delete this agent?',
                            okText: 'Yes',
                            cancelText: 'No',
                            okType: 'danger',
                            onOk: () => handleDelete(record.id),
                        });
                    }
                };

                const menu = (
                    <Menu onClick={handleMenuClick}>
                        <Menu.Item 
                            key="edit"
                        icon={<EditOutlined />}
                    >
                        Edit
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item 
                            key="delete"
                            icon={<DeleteOutlined />}
                            danger
                        >
                            Delete
                        </Menu.Item>
                    </Menu>
                );

                return (
                    <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                        <Button 
                            type="text" 
                            icon={<MoreOutlined />}
                            style={{ fontSize: '18px' }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Agents"
                description="Manage your company agents and their information"
                buttonText="Add Agent"
                buttonIcon={<PlusOutlined />}
                onButtonClick={handleAdd}
            />

            <Spin spinning={initialLoading}>
                <Table
                    columns={columns}
                    dataSource={agents}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        pageSize: 10,
                        showTotal: (total) => `Total ${total} agents`,
                    }}
                />
            </Spin>

            <Modal
                title={null}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setPhotoFileList([]);
                    setSelectedLanguages([]);
                }}
                footer={null}
                centered
                width={900}
                closable={true}
                maskClosable={true}
                className="agent-modal"
                style={{ padding: 0 }}
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {/* Header Section */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        padding: '20px 24px 16px 24px',
                        textAlign: 'center',
                        borderBottom: 'none'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                            <UserOutlined style={{ fontSize: '24px', color: 'white' }} />
                        </div>
                        <Title level={4} style={{ 
                            color: 'white', 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            {editingAgent ? 'Edit Agent' : 'Add New Agent'}
                        </Title>
                        <Text style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '13px',
                            display: 'block',
                            marginTop: '6px'
                        }}>
                            {editingAgent 
                                ? 'Update the agent information below'
                                : 'Fill in the details below to add a new agent to your company'}
                        </Text>
                    </div>

                    {/* Content Section */}
                    <div style={{ padding: '24px' }}>
                        <Alert
                            message={<Text strong style={{ fontSize: '13px' }}>Important Information</Text>}
                            description={
                                <Text style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                                    {editingAgent 
                                        ? 'Make sure all details are accurate. Changes will be reflected immediately.'
                                        : 'All fields marked with * are required. A professional photo helps build trust with customers.'}
                                </Text>
                            }
                            type="info"
                            showIcon
                            icon={<QuestionCircleOutlined />}
                            style={{ 
                                borderRadius: '8px',
                                border: '1px solid #91d5ff',
                                marginBottom: '24px'
                            }}
                        />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="agent_name"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Agent Name</span>
                                        <Tooltip title="Enter the full name of the agent">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Please enter the agent name' }]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Full name of the agent
                                    </Text>
                                }
                            >
                                <Input 
                                    prefix={<UserOutlined style={{ color: '#1890ff' }} />} 
                                    placeholder="e.g., Muhammad Ali Khan"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="designation"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Designation</span>
                                        <Tooltip title="The agent's job title or role in your company">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Please enter the designation' }]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Job title or role (e.g., Sales Agent, Customer Service)
                                    </Text>
                                }
                            >
                                <Input 
                                    prefix={<IdcardOutlined style={{ color: '#1890ff' }} />} 
                                    placeholder="e.g., Sales Agent"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="phone_whatsapp"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Phone / WhatsApp</span>
                                        <Tooltip title="Contact number for the agent. Include country code for WhatsApp">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Please enter phone/WhatsApp number' }]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Include country code (e.g., +92 300 1234567)
                                    </Text>
                                }
                            >
                                <Input 
                                    prefix={<PhoneOutlined style={{ color: '#52c41a' }} />} 
                                    placeholder="+92 300 1234567"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="email"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Email Address</span>
                                        <Tooltip title="Professional email address for the agent">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[
                                    { required: true, message: 'Please enter email address' },
                                    { type: 'email', message: 'Please enter a valid email address' }
                                ]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Professional email address
                                    </Text>
                                }
                            >
                                <Input 
                                    prefix={<MailOutlined style={{ color: '#52c41a' }} />} 
                                    placeholder="agent@example.com"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item 
                        label={
                            <Space size="small">
                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Agent Photo</span>
                                <Tooltip title="Upload a professional photo of the agent. JPG or PNG format, maximum 2 MB">
                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                </Tooltip>
                            </Space>
                        }
                        extra={
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Upload a professional photo (JPG/PNG, ≤ 2 MB). A good photo helps build trust!
                            </Text>
                        }
                    >
                        <Upload
                            listType="picture-card"
                            fileList={photoFileList}
                            onChange={handlePhotoUpload}
                            beforeUpload={() => false}
                            onRemove={() => {
                                setPhotoFileList([]);
                                return true;
                            }}
                            maxCount={1}
                            accept="image/jpeg,image/png,image/jpg"
                            style={{ borderRadius: '8px' }}
                        >
                            {photoFileList.length < 1 && (
                                <div style={{ textAlign: 'center' }}>
                                    <UploadOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                                    <div style={{ marginTop: 8, color: '#595959', fontWeight: 500 }}>Upload Photo</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>

                    <Form.Item 
                        label={
                            <Space size="small">
                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Languages Spoken</span>
                                <Tooltip title="Select all languages this agent can communicate in. This helps match agents with customers">
                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                </Tooltip>
                            </Space>
                        }
                        name="languages_spoken"
                        rules={[{ required: true, message: 'Please select at least one language' }]}
                        extra={
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Select all languages the agent can speak. This helps customers find agents who speak their language!
                            </Text>
                        }
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select languages..."
                            value={selectedLanguages}
                            onChange={(value) => {
                                setSelectedLanguages(value);
                                form.setFieldsValue({ languages_spoken: value });
                            }}
                            size="large"
                            style={{ width: '100%', borderRadius: '8px' }}
                            maxTagCount="responsive"
                        >
                            {languagesOptions.map(lang => (
                                <Option key={lang} value={lang}>{lang}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider style={{ margin: '32px 0', borderColor: '#e8f4f8' }} />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 12
                        }}>
                            <Button
                                onClick={() => {
                                    setIsModalVisible(false);
                                    form.resetFields();
                                    setPhotoFileList([]);
                                    setSelectedLanguages([]);
                                }}
                                size="large"
                                style={{ 
                                    minWidth: 100,
                                    borderRadius: '8px',
                                    height: '44px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                icon={editingAgent ? <SaveOutlined /> : <PlusOutlined />}
                                size="large"
                                style={{ 
                                    minWidth: 180,
                                    borderRadius: '8px',
                                    height: '44px',
                                    fontWeight: 500,
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                            >
                                {loading 
                                    ? (editingAgent ? 'Updating...' : 'Creating...') 
                                    : (editingAgent ? 'Update Agent' : 'Create Agent')}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
                    </div>
                </div>
            <style>{`
                .agent-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .agent-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .agent-modal .ant-modal-close:hover {
                    color: white;
                }
            `}</style>
            </Modal>
        </DashboardLayout>
    );
};

export default Agents;

